import React, { useRef, useState, useEffect } from "react";

import correctSound from "./sounds/correct.mp3";
import errorSound from "./sounds/error.mp3";
import fireworksData from "./animations/fireworks.json";
import GameOverScreen from "../GameOverScreen";
import fireworksSound from "./sounds/fireworks.mp3";
import LineBox from "./LineBox";
import GameLayoutTopbar from "../layouts/GameLayoutTopbar";
import { usePlayClock } from "../common/usePlayClock";
import "./FindMiddleForm.css";

import {
  startSession,
  endSession,
  getLevelsByGame,
  setCurrentGameLevelForUser,
} from "../../services/GameService";

// Toggle: when true, each round (after the first) shifts the line of boxes
// left or right by a random 5% of its own width.
const OFFSET_ENABLED = true;

// Above this many boxes the line drops its fixed gaps and box borders thin out,
// so long (endless) lines still fit on a phone.
const DENSE_BOXES = 21;

const pickOffset = () => {
  if (!OFFSET_ENABLED) return 0;
  const direction = Math.random() < 0.5 ? -1 : 1;
  const magnitude = Math.random() * 5;
  return direction * magnitude;
};

// 1-indexed position of an odd box count in the full 3,5,7,... sequence
// (box 3 -> 1, box 5 -> 2, ...). Used to seed the score so it means the same
// "how far did you get" thing regardless of which band a run starts in.
const sequencePosition = (boxes) => (boxes - 1) / 2;

// Where a correct click on a board of `boxes` boxes in band `bandNumber` leads.
// `bands` maps band number -> { startBoxes, maxBoxes, step }; `bandNumbers` is
// the sorted list of band numbers. Returns { band, boxes, clearedBand }:
//   * below the band's max: the same band, `step` more boxes;
//   * at the band's max: cross into the next band's start (clearedBand), or,
//     on the last band, keep going — the last band is endless;
//   * past the last band's max (endless): keep adding `step` boxes.
// Returns null if `bandNumber` isn't in the table.
export const advanceAfterCorrect = (boxes, bandNumber, bands, bandNumbers) => {
  const band = bands.get(bandNumber);
  if (!band) return null;
  const grow = { band: bandNumber, boxes: boxes + band.step, clearedBand: false };
  if (boxes < band.maxBoxes) return grow;
  // The first board at (or just past) the max clears the band; later boards
  // only happen on the endless last band and don't clear it again.
  const crossing = boxes < band.maxBoxes + band.step;
  const next = bandNumbers.find((b) => b > bandNumber);
  if (crossing && next != null) {
    return { band: next, boxes: bands.get(next).startBoxes, clearedBand: true };
  }
  return { ...grow, clearedBand: crossing };
};

const FindMiddleForm = ({ }) => {

  const [squares, setSquares] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [checks, setChecks] = useState([]);
  const [showFireworks, setShowFireworks] = useState(false);
  const [isGameReady, setIsGameReady] = useState(false);
  const [levelNumber, setLevelNumber] = useState(1);
  const [currentBoxes, setCurrentBoxes] = useState(0);
  const [lineOffset, setLineOffset] = useState(0);

  const correctSoundRef = useRef(new Audio(correctSound));
  const errorSoundRef = useRef(new Audio(errorSound));
  const fireworksAudioRef = useRef(new Audio(fireworksSound));

  const sessionIdRef = useRef(null);
  const gameIdRef = useRef(null);
  // One clock per run. A run can cross several bands, but its session (and so
  // all its play time) carries the band it started in.
  const playClock = usePlayClock();
  const timeBetweenRoundsRef = useRef(1200);
  const roundOverRef = useRef(false); // guards against double-clicks within a round

  // Box-count bands, keyed by DB level number: { startBoxes, maxBoxes, step }.
  const bandsRef = useRef(new Map());
  const bandNumbersRef = useRef([]); // band numbers present, ascending

  const currentBoxesRef = useRef(0);
  const currentBandRef = useRef(1); // band of the board currently shown
  const runStartBandRef = useRef(1); // band the run began at
  const clearedBandsRef = useRef(0); // bands fully cleared this run
  const scoreRef = useRef(0); // sequence-position score (+1 per correct click)
  const finalBoxesRef = useRef(0); // box count reached, for the game-over screen
  const nextStartBoxesRef = useRef(0); // boxes the next run will start from
  const endlessRef = useRef(false); // past the last band's max this run
  // The previous run's save (score + new level). A restart waits for it so the
  // next run starts on the level that run earned.
  const savingRef = useRef(null);

  useEffect(() => {
    const initGame = async () => {
      await startNewGame();
    };
    initGame();

    return () => {
      if (correctSoundRef.current) {
        correctSoundRef.current.pause();
        correctSoundRef.current.currentTime = 0;
      }
      if (errorSoundRef.current) {
        errorSoundRef.current.pause();
        errorSoundRef.current.currentTime = 0;
      }
      if (fireworksAudioRef.current) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  // Begin a session at the user's current band and seed the game state.
  const startNewGame = async () => {
    setGameOver(false);
    setShowFireworks(false);
    setIsGameReady(false);
    roundOverRef.current = false;
    endlessRef.current = false;

    if (savingRef.current) {
      await savingRef.current; // never rejects (errors are caught in finishRun)
      savingRef.current = null;
    }

    const sessionParams = await startSession("find-the-middle");

    if (!sessionParams || sessionParams.error || !sessionParams.level) {
      alert(sessionParams?.error || "Could not start session: Game not found.");
      return;
    }

    sessionIdRef.current = sessionParams.sessionId || null;

    const currentLevel = sessionParams.level;
    gameIdRef.current = currentLevel.game;

    const params = currentLevel.level_parameters
      ? JSON.parse(currentLevel.level_parameters)
      : {};
    timeBetweenRoundsRef.current = params.timeBetweenRounds ?? 1200;

    // Build the band table from the game's levels (box ranges live in the DB,
    // seeded from core/data/game_levels.json).
    const allLevels = await getLevelsByGame(currentLevel.game);
    const bands = new Map();
    (allLevels || []).forEach((lvl) => {
      const p = lvl.level_parameters ? JSON.parse(lvl.level_parameters) : {};
      const startBoxes = p.startBoxes ?? 3;
      bands.set(lvl.level_number, {
        startBoxes,
        maxBoxes: p.maxBoxes ?? startBoxes,
        step: p.step ?? 2,
      });
    });
    // Fallback so the game stays playable if the level list didn't load.
    if (!bands.has(currentLevel.level_number)) {
      const startBoxes = params.startBoxes ?? 3;
      bands.set(currentLevel.level_number, {
        startBoxes,
        maxBoxes: params.maxBoxes ?? startBoxes,
        step: params.step ?? 2,
      });
    }
    bandsRef.current = bands;
    // Band numbers can be sparse (a level may be deleted or renumbered), so
    // drive crossing/demotion off the sorted list rather than assuming 1..N.
    bandNumbersRef.current = [...bands.keys()].sort((a, b) => a - b);

    // Seed the run at the user's current band.
    const startBand = currentLevel.level_number;
    runStartBandRef.current = startBand;
    currentBandRef.current = startBand;
    setLevelNumber(startBand);
    clearedBandsRef.current = 0;

    const startBoxes = bands.get(startBand).startBoxes;
    // Score starts at the starting board's sequence position and climbs +1 per
    // correct click. Min is 1 (never 0) and it stays well under 100 in expected
    // play, which keeps endSession's generic threshold path inert (see finishRun).
    scoreRef.current = sequencePosition(startBoxes);
    setBoard(startBoxes, 0);

    setIsGameReady(true);
    playClock.start();
  };

  const buildSquares = (count) => {
    const boxes = [];
    for (let i = 0; i < count; i++) {
      boxes.push(i);
    }
    return boxes;
  };

  // Render a fresh board of `count` boxes. `offset` defaults to a fresh random
  // shift; pass 0 for the very first board of a run.
  const setBoard = (count, offset = pickOffset()) => {
    currentBoxesRef.current = count;
    setCurrentBoxes(count);
    setSquares(buildSquares(count));
    setChecks(Array(count).fill(null));
    setLineOffset(offset);
  };

  // Previous band number that actually exists (handles sparse numbering).
  const prevBandNumber = (n) => {
    let prev = null;
    for (const b of bandNumbersRef.current) {
      if (b < n) prev = b;
      else break;
    }
    return prev;
  };

  // End the run: record the score and set the resulting level explicitly.
  //
  // We do NOT let GameService.endSession auto-promote/demote, because one run
  // can span several bands and the generic ±1 threshold path can't express
  // that. With the levels' thresholds at 0/100 that path stays inert as long as
  // the score is in (0, 100): the sequence-position score is >= 1 (never trips
  // the score<=0 demote) and stays well under 100 in expected play — the
  // endless last band would need ~200 boxes to reach it.
  const finishRun = (didWin) => {
    finalBoxesRef.current = currentBoxesRef.current;

    const cleared = clearedBandsRef.current;
    const startBand = runStartBandRef.current;
    // Cleared nothing -> demote to the previous existing band (if any).
    // Otherwise resume at the band actually reached this run.
    const newLevel =
      cleared === 0
        ? (prevBandNumber(startBand) ?? startBand)
        : currentBandRef.current;

    // Box count the next run will open on, shown on the game-over screen.
    nextStartBoxesRef.current =
      bandsRef.current.get(newLevel)?.startBoxes ?? finalBoxesRef.current;

    const finishingSessionId = sessionIdRef.current;
    const finalScore = scoreRef.current;
    const playedMs = playClock.stop(); // a miss has already stopped it
    sessionIdRef.current = null;

    if (finishingSessionId) {
      savingRef.current = (async () => {
        try {
          await endSession(finishingSessionId, finalScore, playedMs);
          await setCurrentGameLevelForUser(gameIdRef.current, newLevel);
        } catch (err) {
          console.error("Error finishing find-the-middle run:", err);
        }
      })();
    }

    if (didWin) setShowFireworks(true);
    setGameOver(true);
  };

  const handleClockSelect = (index) => {
    // Ref guard (not state): two clicks in the same frame would both pass a
    // state-based check before React re-rendered, double-firing the round.
    if (roundOverRef.current || gameOver || !isGameReady) {
      return;
    }

    roundOverRef.current = true;

    const boxesThisRound = currentBoxesRef.current;
    const centerPoint = (boxesThisRound - 1) / 2;
    const isCorrect = centerPoint === index;

    const newChecks = Array(boxesThisRound).fill(null);
    if (isCorrect) {
      newChecks[centerPoint] = "correct";
    } else {
      newChecks[centerPoint] = "target";
      newChecks[index] = index < centerPoint ? "left" : "right";
    }
    setChecks(newChecks);

    (isCorrect ? correctSoundRef : errorSoundRef).current?.play().catch((e) =>
      console.error("Failed to play sound:", e),
    );

    if (!isCorrect) {
      // Wrong click: pause so the markers show, then end the run. A miss is the
      // only way a run ends; getting past the last band's max counts as a win.
      // It's the run's last answer, so play time stops here.
      playClock.stop();
      setTimeout(() => {
        roundOverRef.current = false;
        finishRun(endlessRef.current);
      }, timeBetweenRoundsRef.current);
      return;
    }

    // Correct click: hold briefly so the green marker renders, then grow the
    // board, silently cross into the next band, or — past the last band's max —
    // keep growing until the player misses.
    setTimeout(() => {
      scoreRef.current += 1;
      const next = advanceAfterCorrect(
        boxesThisRound,
        currentBandRef.current,
        bandsRef.current,
        bandNumbersRef.current,
      );
      if (!next) {
        // Current band missing from the table (shouldn't happen) — end safely.
        roundOverRef.current = false;
        finishRun(true);
        return;
      }
      if (next.clearedBand) clearedBandsRef.current += 1;
      if (next.band !== currentBandRef.current) {
        // Silently cross into the next band.
        currentBandRef.current = next.band;
        setLevelNumber(next.band);
      } else if (next.clearedBand) {
        endlessRef.current = true; // cleared the last band: now endless
      }
      setBoard(next.boxes);
      roundOverRef.current = false;
    }, timeBetweenRoundsRef.current);
  };

  useEffect(() => {
    if (showFireworks) {
      fireworksAudioRef.current.loop = true;
      fireworksAudioRef.current.play().catch((error) => {
        console.error("Failed to play fireworks sound:", error);
      });
    } else {
      fireworksAudioRef.current.pause();
      fireworksAudioRef.current.currentTime = 0;
    }

    return () => {
      if (showFireworks) {
        fireworksAudioRef.current.pause();
        fireworksAudioRef.current.currentTime = 0;
      }
    };
  }, [showFireworks]);

  if (gameOver) {
    return (
      <GameLayoutTopbar
        infoPanel={
          <h2 className="heading-secondary">Find The Middle - Game Over</h2>
        }
        gameArea={
          <GameOverScreen
            stats={[
              `You reached ${finalBoxesRef.current} boxes`,
              `Continue playing from ${nextStartBoxesRef.current} boxes?`,
            ]}
            onRestart={startNewGame}
            showFireworks={showFireworks}
            fireworksAnimation={fireworksData}
          />
        }
      />
    );
  }

  return (
    <GameLayoutTopbar
      infoPanel={
        <>
          <h2 className="heading-secondary">Level {levelNumber}</h2>
          <h3 className="heading-tertiary">
            Find The Middle - {currentBoxes} boxes
          </h3>
          <p className="text-muted">Click on the center box</p>
        </>
      }
      gameArea={
        <div
          className={`square-line${OFFSET_ENABLED ? ' square-line--offset' : ''}${
            currentBoxes > DENSE_BOXES ? ' square-line--dense' : ''
          }`}
          style={{ transform: `translateX(${lineOffset}%)` }}
        >
          {squares.map((index) => (
            <LineBox
              key={index}
              totalBoxes={currentBoxes}
              label={`Box ${index + 1} of ${currentBoxes}`}
              checkBox={checks[index]}
              onClick={() => handleClockSelect(index)}
            />
          ))}
        </div>
      }
    />
  );
};

export default FindMiddleForm;
